import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { extractPassportImgFromPdfRequest } from 'helpers/api-requests/admin';
import { getFileExtension, converBase64toBlob } from 'hooks/usePassportScanner';
import { maxUploadSize } from 'helpers/jwt_helper';
import './PassportScanner.scss';

const TO_RADIANS = Math.PI / 180;

const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
};

const canvasPreview = (image, canvas, crop, scale = 1, rotate = 0) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;
  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
  );
  ctx.restore();
};

const useDebounceEffect = (fn, waitTime, deps) => {
  useEffect(() => {
    const t = setTimeout(() => fn.apply(undefined, deps), waitTime);
    return () => clearTimeout(t);
  }, [deps]);
};

export const PassportScanner = ({
  onScan,
  isLoading,
  error: externalError,
  onClearError,
  success: externalSuccess,
  onClearSuccess,
}) => {
  const { t } = useTranslation();

  // State
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale] = useState(1);
  const [rotate] = useState(0);
  const [aspect] = useState(null);
  const [showSubmitBtn, setShowSubmitBtn] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Refs
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Get max upload size from JWT token
  const MAX_FILE_SIZE = maxUploadSize();
  const maxSizeMB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

  // Use external or local error
  const displayError = externalError || localError;
  const displaySuccess = externalSuccess;

  // Clear error
  const clearError = () => {
    setLocalError(null);
    if (onClearError) onClearError();
  };

  // Clear success
  const clearSuccess = () => {
    if (onClearSuccess) onClearSuccess();
  };

  // Clear both
  const clearAllMessages = () => {
    clearError();
    clearSuccess();
  };

  // Convert PDF to Image
  const convertPdfToImage = async file => {
    try {
      clearAllMessages();

      const response = await extractPassportImgFromPdfRequest({ file: file });
      if (response?.success && response.data?.base64) {
        const blob = await converBase64toBlob(
          response.data.base64,
          'image/png',
        );
        return blob;
      } else {
        setLocalError(response?.message || t('passport.pdfExtractionFailed'));
        return null;
      }
    } catch (error) {
      console.error('PDF extraction error:', error);
      setLocalError(error.message || t('passport.pdfExtractionFailed'));
      return null;
    }
  };

  // Handle file selection
  const onSelectFile = async e => {
    setCompletedCrop(null);
    setShowSubmitBtn(true);
    clearAllMessages();

    if (e.target && e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0];

      if (!file) {
        setLocalError(t('passport.fileReadError'));
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setLocalError(t('passport.fileSizeExceeded', { maxSize: maxSizeMB }));
        return;
      }

      // Check file type
      const validTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/pdf',
      ];
      if (!validTypes.includes(file.type)) {
        setLocalError(t('passport.invalidFileType'));
        return;
      }

      setCrop(undefined);
      setFileName(file.name);

      const fileExtension = getFileExtension(file.name).toLowerCase();

      // Handle PDF files
      if (fileExtension === 'pdf') {
        const imageBlob = await convertPdfToImage(file);
        if (imageBlob) {
          const reader = new FileReader();
          reader.addEventListener('load', () => {
            const result = reader.result?.toString() || '';
            setImgSrc(result);
            clearAllMessages();
          });
          reader.addEventListener('error', () => {
            console.error('FileReader error:', error);
            setLocalError(t('passport.fileReadError'));
          });
          reader.readAsDataURL(imageBlob);
        }
        return;
      }

      // Handle images
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const result = reader.result?.toString() || '';
        setImgSrc(result);
        clearAllMessages();
      });
      reader.addEventListener('error', () => {
        console.error('FileReader error:', error);
        setLocalError(t('passport.fileReadError'));
      });
      reader.readAsDataURL(file);
    }
  };

  // Drag and drop handlers
  const onDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    clearAllMessages();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fakeEvent = { target: { files: e.dataTransfer.files } };
      onSelectFile(fakeEvent);
    }
  };

  const onDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onImageLoad = e => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 0.5));
  };

  const onSubmitScan = async () => {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;

    if (!image || !previewCanvas || !completedCrop) {
      setLocalError(t('passport.cropRequired'));
      return;
    }

    try {
      clearAllMessages();

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const offscreen = new OffscreenCanvas(
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
      );
      const ctx = offscreen.getContext('2d');
      if (!ctx) throw new Error('No 2d context');

      ctx.drawImage(
        previewCanvas,
        0,
        0,
        previewCanvas.width,
        previewCanvas.height,
        0,
        0,
        offscreen.width,
        offscreen.height,
      );

      const blob = await offscreen.convertToBlob({
        type: 'image/jpeg',
        quality: 0.9,
      });

      // Only send to parent - success will come from the hook
      onScan(blob);
    } catch (error) {
      console.error('Error processing image:', error);
      setLocalError(t('passport.processingError'));
    }
  };

  const onReset = () => {
    setImgSrc('');
    setCrop(null);
    setCompletedCrop(null);
    setFileName('');
    setShowSubmitBtn(false);
    clearAllMessages();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useDebounceEffect(
    () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
        );
      }
    },
    100,
    [completedCrop, scale, rotate],
  );

  // Render success message
  const renderSuccess = () => {
    if (!displaySuccess) return null;

    return (
      <div className="passport-scanner__success">
        <i className="bx bx-check-circle"></i>
        <div className="passport-scanner__success-content">
          <span
            dangerouslySetInnerHTML={{ __html: t('passport.scanSuccess') }}
          />
        </div>
        <button
          className="passport-scanner__success-close"
          onClick={clearSuccess}
          disabled={isLoading}
          aria-label="Dismiss success message"
          type="button"
        >
          <i className="bx bx-x"></i>
        </button>
      </div>
    );
  };

  // Render error message
  const renderError = () => {
    if (!displayError) return null;

    return (
      <div className="passport-scanner__error">
        <i className="bx bx-error-circle"></i>
        <span>{displayError}</span>
        <button
          className="passport-scanner__error-close"
          onClick={clearError}
          disabled={isLoading}
          aria-label="Dismiss error message"
          type="button"
        >
          <i className="bx bx-x"></i>
        </button>
      </div>
    );
  };

  // Render upload area
  const renderUploadArea = () => (
    <div
      className={`passport-scanner__upload ${isDragging ? 'dragging' : ''} ${
        displayError ? 'has-error' : ''
      }`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onClick={e => {
        // Only trigger file input if clicking on the div itself
        // but NOT if clicking on any button inside it
        if (e.target === e.currentTarget || !e.target.closest('button')) {
          fileInputRef.current?.click();
        }
      }}
    >
      <div className="passport-scanner__upload-content">
        <i className="bx bx-cloud-upload passport-scanner__upload-icon"></i>
        <p className="passport-scanner__upload-text">
          {t('passport.uploadOrDrop')}
        </p>
        <p className="passport-scanner__upload-hint">
          {t('passport.uploadHint', { maxSize: maxSizeMB })}
        </p>
        <button className="passport-scanner__upload-btn" type="button">
          <i className="bx bx-upload"></i>
          {t('passport.chooseFile')}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={onSelectFile}
        className="passport-scanner__file-input"
      />
    </div>
  );

  // Render image preview and crop
  const renderPreview = () => (
    <div className="passport-scanner__preview">
      <div className="passport-scanner__preview-header">
        <span className="passport-scanner__file-name">
          <i className="bx bx-file"></i>
          {fileName || 'Image'}
        </span>
        <button
          className="passport-scanner__close-btn"
          onClick={onReset}
          disabled={isLoading}
          aria-label="Remove image"
          type="button"
        >
          <i className="bx bx-x"></i>
        </button>
      </div>

      <div className="passport-scanner__crop-container">
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => {
            if (percentCrop?.width && percentCrop?.height) {
              setCrop(percentCrop);
            } else {
              setCrop({
                unit: percentCrop?.unit || '%',
                x: 0,
                y: 0,
                width: 100,
                height: 100,
              });
            }
            setShowSubmitBtn(true);
            clearAllMessages();
          }}
          onComplete={c => setCompletedCrop(c)}
          aspect={aspect}
          minWidth={200}
          minHeight={50}
          className="passport-scanner__crop"
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={imgSrc}
            style={{
              transform: `scale(${scale}) rotate(${rotate}deg)`,
              maxHeight: '400px',
              width: 'auto',
            }}
            onLoad={onImageLoad}
            className="passport-scanner__image"
          />
        </ReactCrop>
      </div>
    </div>
  );

  const renderActions = () => {
    if (!completedCrop || !showSubmitBtn) return null;

    return (
      <div className="passport-scanner__actions">
        <button
          className="passport-scanner__btn passport-scanner__btn--secondary"
          onClick={onReset}
          disabled={isLoading}
          type="button"
        >
          <i className="bx bx-refresh"></i>
          {t('common.reset')}
        </button>
        <button
          className="passport-scanner__btn passport-scanner__btn--primary"
          onClick={onSubmitScan}
          disabled={isLoading}
          type="button"
        >
          {isLoading ? (
            <>
              <i className="bx bx-loader-alt bx-spin"></i>
              {t('common.processing')}
            </>
          ) : displaySuccess ? (
            <>
              <i className="bx bx-rotate-right"></i>
              {t('passport.resubmit')}
            </>
          ) : (
            <>
              <i className="bx bx-check"></i>
              {t('common.submit')}
            </>
          )}
        </button>
      </div>
    );
  };

  // Render hidden canvas for preview
  const renderHiddenCanvas = () => {
    if (!completedCrop) return null;

    return (
      <canvas
        ref={previewCanvasRef}
        style={{
          border: '1px solid black',
          objectFit: 'contain',
          width: completedCrop.width,
          height: completedCrop.height,
          display: 'none',
        }}
      />
    );
  };

  return (
    <div className="passport-scanner w-100">
      {/* Header */}
      <div className="passport-scanner__header">
        <h5 className="passport-scanner__title">
          <i className="bx bx-scan"></i>
          {t('passport.scanPassport')}
        </h5>
        <span className="passport-scanner__badge">
          <i className="bx bx-info-circle"></i>
          {t('passport.supportedFormats')}
          <span className="passport-scanner__size-limit">
            (Max: {maxSizeMB}MB)
          </span>
        </span>
      </div>

      {/* Success Message - Green background */}
      {renderSuccess()}

      {/* Error Message - Red background */}
      {renderError()}

      {/* Content */}
      {!imgSrc ? (
        renderUploadArea()
      ) : (
        <>
          {renderPreview()}
          {renderActions()}
          {renderHiddenCanvas()}
        </>
      )}
    </div>
  );
};
